/**
 * ProtectedRoute component.
 *
 * Wraps a route's children and redirects to /login if the user is not
 * authenticated. While AuthContext initialises (loading === true) it renders
 * nothing to avoid a flash redirect on page refresh.
 *
 * Authorization por rol (docs/seguridad.md):
 * Si se pasa `allowedRoles`, el usuario debe tener uno de esos roles
 * efectivos (respeta jerarquía: un superadministrador pasa como administrador).
 * Si no cumple, se redirige a `/home`. Sin la prop, solo exige autenticación
 * (comportamiento previo). El backend también valida rol por endpoint, pero
 * este guard evita que un cliente vea la UI privilegiada y filtre datos
 * secundarios (nombres, IDs) antes de recibir un 403.
 *
 * Usage en App.tsx:
 *   <Route path="/home" element={<ProtectedRoute><Home /></ProtectedRoute>} />
 *   <Route
 *     path="/backup"
 *     element={
 *       <ProtectedRoute allowedRoles={["superadministrador"]}>
 *         <BackupPage />
 *       </ProtectedRoute>
 *     }
 *   />
 */

import React from "react";
import { Navigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { normalizeRole, UserRole } from "../utils/roles.utils";

interface ProtectedRouteProps {
  children: React.ReactNode;
  allowedRoles?: UserRole[];
}

function isTokenExpired(token: string): boolean {
  try {
    const payload = JSON.parse(atob(token.split(".")[1]));
    return payload.exp * 1000 < Date.now();
  } catch {
    return true;
  }
}

// Un superadministrador hereda los permisos de administrador.
const effectiveRoles = (rol: string | string[] | undefined | null): Set<UserRole> => {
  const normalized = normalizeRole(rol);
  const set = new Set<UserRole>([normalized]);
  if (normalized === "superadministrador") set.add("administrador");
  return set;
};

const ProtectedRoute: React.FC<ProtectedRouteProps> = ({ children, allowedRoles }) => {
  const { token, loading, logout, user } = useAuth();

  // Wait for AuthContext to finish reading from storage
  if (loading) return null;

  if (!token) return <Navigate to="/login" replace />;

  if (isTokenExpired(token)) {
    logout();
    return <Navigate to="/login" replace />;
  }

  if (allowedRoles && allowedRoles.length > 0) {
    const roles = effectiveRoles(user?.rol);
    const allowed = allowedRoles.some((r) => roles.has(r));
    if (!allowed) {
      return <Navigate to="/home" replace />;
    }
  }

  return <>{children}</>;
};

export default ProtectedRoute;
